import {
  attachClientErrorListeners,
  attachServerErrorListeners,
} from "../core/capture.js";
import type { TelemetryClient } from "../core/client.js";
import type { TelemetryModule } from "../di/define-module.js";
import { unbindClient } from "../runtime/client.js";
import { unbindServer } from "../runtime/server.js";
import { createRuntimeFromPlan } from "./create-runtime.js";
import type {
  ClientInitResult as BaseClientInitResult,
  ServerInitResult as BaseServerInitResult,
} from "./init-result.js";
import type { PlatformBuildContext } from "./platform-context.js";
import {
  buildTelemetryPlan,
  planExpectsClient,
  planExpectsServer,
  type TelemetryPlan,
} from "./plan.js";
import type { CreateTelemetryInput } from "./plan-input.js";
import {
  mergeClientExtensions,
  mergeServerExtensions,
} from "../providers/init-extras/registry.js";

export type { CreateTelemetryInput };

export type ClientInitResult<TModule extends TelemetryModule = TelemetryModule> =
  BaseClientInitResult & { readonly module: TModule };

export type ServerInitResult<TModule extends TelemetryModule = TelemetryModule> =
  BaseServerInitResult & { readonly module: TModule };

export interface TelemetryHandle<TModule extends TelemetryModule = TelemetryModule> {
  readonly plan: TelemetryPlan<TModule>;
  get module(): TModule;
  initClient(): Promise<ClientInitResult<TModule>>;
  initServer(): Promise<ServerInitResult<TModule>>;
}

class TelemetryHandleImpl<TModule extends TelemetryModule> implements TelemetryHandle<TModule> {
  readonly plan: TelemetryPlan<TModule>;
  #client: TelemetryClient | undefined;
  #server: TelemetryClient | undefined;
  #module: TModule | undefined;

  constructor(plan: TelemetryPlan<TModule>) {
    this.plan = plan;
  }

  get module(): TModule {
    return this.#buildModule();
  }

  async initClient(): Promise<ClientInitResult<TModule>> {
    if (!planExpectsClient(this.plan)) {
      throw new Error(
        '[telemetry] This configuration has no client runtime (runtime: "server" only).',
      );
    }
    if (!this.#client) {
      this.#client = createRuntimeFromPlan(this.plan, "client");
      await this.#client.prepareProviders();
    }

    const api = this.#client;
    const module = this.#buildModule();
    let detachListeners: (() => void) | undefined;
    const handle = this;

    return {
      runtime: "client" as const,
      api,
      module,
      extensions: mergeClientExtensions(this.plan),
      listenUnhandledErrors() {
        detachListeners = attachClientErrorListeners(api);
        return detachListeners;
      },
      dispose() {
        detachListeners?.();
        detachListeners = undefined;
        handle.#client = undefined;
        handle.#module = undefined;
        unbindClient();
      },
    };
  }

  async initServer(): Promise<ServerInitResult<TModule>> {
    if (!planExpectsServer(this.plan)) {
      throw new Error(
        "[telemetry] This configuration has no server runtime. Set ssr: true or add server providers.",
      );
    }
    if (!this.#server) {
      this.#server = createRuntimeFromPlan(this.plan, "server");
      await this.#server.prepareProviders();
    }

    const api = this.#server;
    const module = this.#buildModule();
    let detachListeners: (() => void) | undefined;
    const handle = this;

    return {
      runtime: "server" as const,
      api,
      module,
      extensions: mergeServerExtensions(this.plan),
      listenUnhandledErrors() {
        detachListeners = attachServerErrorListeners(api);
        return detachListeners;
      },
      dispose() {
        detachListeners?.();
        detachListeners = undefined;
        handle.#server = undefined;
        handle.#module = undefined;
        unbindServer();
      },
    };
  }

  #buildModule(): TModule {
    if (!this.#module) {
      const ctx: PlatformBuildContext<TModule> = {
        plan: this.plan,
        getClient: () => this.#client,
        getServer: () => this.#server,
      };
      this.#module = this.plan.platform.buildModule(ctx) as TModule;
    }
    return this.#module;
  }
}

export function createTelemetry<TModule extends TelemetryModule = TelemetryModule>(
  input: CreateTelemetryInput<TModule>,
): TelemetryHandle<TModule> {
  const plan = buildTelemetryPlan(input);
  return new TelemetryHandleImpl(plan);
}
